import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { useDataAccumulatorForSingleOrder } from "../../context/dataAccumulatorForSingleOrder";
import { MedicalQuestion } from "../../data-model/medicalQuestion";
import useFetchQuestionnaire from "../../hooks/useFetchQuestionnaire";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import PortalQuestionnaire from "./components/portalQuestionnaire";
import PortalQuestionnaireAttachment from "./components/portalQuestionnaireAttachment";
import PortalQuestionnaireFooter from "./components/portalQuestionnaireFooter";
import PortalQuestionnaireHeader from "./components/portalQuestionnaireHeader";

const PortalQuestionnaireLayout = () => {
  const { id } = useParams();
  const orgId = getOrgIdForFetchExternalWorklist();
  const { generalOrderDetailsLoading, fetchAll } =
    useDataAccumulatorForSingleOrder();
  const { data, loading, error, hasQuestions } = useFetchQuestionnaire({
    orderId: id ?? "",
  });
  const [mergedPdfToRender, setMergedPdfToRender] = useState<File | null>(null);
  const questionnaireAnswers = useRef<MedicalQuestion[]>([]);
  const isDirtyRef = useRef<boolean>(false);

  const handleQuestionnaireUpdated = useCallback(
    (answers: MedicalQuestion[], isDirty: boolean) => {
      questionnaireAnswers.current = answers;
      isDirtyRef.current = isDirty;
    },
    [],
  );

  useEffect(() => {
    if (id) {
      fetchAll(id, orgId ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (generalOrderDetailsLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoaderMessage message="Fetching questionnaire details..." />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Header */}
      <div className="portal-questionnaire-header flex items-center gap-2 px-4 py-3">
        <PortalQuestionnaireHeader mergedPdfToRender={setMergedPdfToRender} />
      </div>

      {/* Body */}
      <div className="portal-questionnaire-body flex flex-1 overflow-hidden px-4 py-2">
        <div className="body-inside-container flex h-full w-full overflow-hidden rounded-lg border border-primaryGray-14">
          <div className="w-1/2 overflow-hidden border-r border-primaryGray-14">
            <PortalQuestionnaireAttachment
              mergedPdfToRender={mergedPdfToRender}
            />
          </div>
          <div className="h-full w-1/2 overflow-auto p-2">
            <PortalQuestionnaire
              id={id ?? ""}
              onQuestionnaireUpdated={handleQuestionnaireUpdated}
              data={data}
              loading={loading}
              error={!!error}
              hasQuestions={hasQuestions}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="portal-questionnaire-footer">
        <PortalQuestionnaireFooter
          getQuestionnaireAnswers={() => questionnaireAnswers.current}
          loading={loading}
          error={!!error}
          hasQuestions={hasQuestions}
          getIsDirty={() => isDirtyRef.current}
        />
      </div>
    </div>
  );
};

export default PortalQuestionnaireLayout;
